import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { organisationAPI } from '../api/services';
import { Organisation, OrgPlan, OrgSettings } from '../types';

interface OrganisationState {
  organisations: Organisation[];
  currentOrganisation: Organisation | null;
  loading: boolean;
  error: string | null;
  filters: { isActive?: boolean; plan?: string };
  pagination: { page: number; limit: number; total: number; pages: number };
}

const initialState: OrganisationState = {
  organisations: [],
  currentOrganisation: null,
  loading: false,
  error: null,
  filters: {},
  pagination: { page: 1, limit: 20, total: 0, pages: 0 },
};

export const fetchOrganisations = createAsyncThunk(
  'organisations/fetchAll',
  async (params: { isActive?: boolean; plan?: string; page?: number; limit?: number } = {}, { rejectWithValue }) => {
    try {
      const res = await organisationAPI.getAll(params);
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch organisations');
    }
  }
);

export const fetchOrganisationById = createAsyncThunk(
  'organisations/fetchById',
  async (id: string, { rejectWithValue }) => {
    try {
      const res = await organisationAPI.getById(id);
      return res.data.organisation as Organisation;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch organisation');
    }
  }
);

export const createOrganisation = createAsyncThunk(
  'organisations/create',
  async (data: Partial<Organisation> & { adminUser?: { name: string; email: string; password: string; phone?: string } }, { rejectWithValue }) => {
    try {
      const res = await organisationAPI.create(data);
      // res.data = { organisation, adminUser? }
      return res.data as { organisation: Organisation; adminUser?: { _id: string; name: string; email: string; phone?: string; isFirstLogin: boolean } };
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to create organisation');
    }
  }
);

export const updateOrganisation = createAsyncThunk(
  'organisations/update',
  async ({ id, data }: { id: string; data: Partial<Organisation> }, { rejectWithValue }) => {
    try {
      const res = await organisationAPI.update(id, data);
      return res.data.organisation as Organisation;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to update organisation');
    }
  }
);

export const changeOrgPlan = createAsyncThunk(
  'organisations/changePlan',
  async ({ id, plan }: { id: string; plan: OrgPlan }, { rejectWithValue }) => {
    try {
      const res = await organisationAPI.changePlan(id, plan);
      return res.data.organisation as Organisation;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to change plan');
    }
  }
);

export const updateOrgSettings = createAsyncThunk(
  'organisations/updateSettings',
  async ({ id, settings }: { id: string; settings: Partial<OrgSettings> }, { rejectWithValue }) => {
    try {
      const res = await organisationAPI.updateSettings(id, settings);
      return res.data.settings as OrgSettings;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to update settings');
    }
  }
);

export const addOrgAdmin = createAsyncThunk(
  'organisations/addAdmin',
  async ({ id, userId }: { id: string; userId: string }, { rejectWithValue }) => {
    try {
      const res = await organisationAPI.addAdmin(id, userId);
      return res.data.organisation as Organisation;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to add admin');
    }
  }
);

export const createAndAddOrgAdmin = createAsyncThunk(
  'organisations/createAndAddAdmin',
  async ({ id, data }: { id: string; data: { name: string; email: string; password: string; phone?: string } }, { rejectWithValue }) => {
    try {
      const res = await organisationAPI.createAdmin(id, data);
      return res.data.organisation as Organisation;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to create admin');
    }
  }
);

export const removeOrgAdmin = createAsyncThunk(
  'organisations/removeAdmin',
  async ({ id, userId }: { id: string; userId: string }, { rejectWithValue }) => {
    try {
      const res = await organisationAPI.removeAdmin(id, userId);
      return res.data.organisation as Organisation;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to remove admin');
    }
  }
);

export const addOrgDomain = createAsyncThunk(
  'organisations/addDomain',
  async ({ id, domain, isPrimary }: { id: string; domain: string; isPrimary?: boolean }, { rejectWithValue }) => {
    try {
      const res = await organisationAPI.addDomain(id, domain, isPrimary);
      return res.data as { domains: Organisation['domains'] };
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to add domain');
    }
  }
);

export const removeOrgDomain = createAsyncThunk(
  'organisations/removeDomain',
  async ({ id, domain }: { id: string; domain: string }, { rejectWithValue }) => {
    try {
      const res = await organisationAPI.removeDomain(id, domain);
      return res.data as { domains: Organisation['domains'] };
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to remove domain');
    }
  }
);

export const suspendOrganisation = createAsyncThunk(
  'organisations/suspend',
  async ({ id, reason }: { id: string; reason?: string }, { rejectWithValue }) => {
    try {
      await organisationAPI.suspend(id, reason);
      return id;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to suspend organisation');
    }
  }
);

export const reinstateOrganisation = createAsyncThunk(
  'organisations/reinstate',
  async (id: string, { rejectWithValue }) => {
    try {
      await organisationAPI.reinstate(id);
      return id;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to reinstate organisation');
    }
  }
);

const organisationSlice = createSlice({
  name: 'organisations',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {};
    },
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentOrganisation: (state) => {
      state.currentOrganisation = null;
    },
  },
  extraReducers: (builder) => {
    // fetchAll
    builder
      .addCase(fetchOrganisations.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchOrganisations.fulfilled, (state, action) => {
        state.loading = false;
        state.organisations = action.payload.organisations;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchOrganisations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // fetchById
    builder
      .addCase(fetchOrganisationById.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchOrganisationById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentOrganisation = action.payload;
      })
      .addCase(fetchOrganisationById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // create
    builder
      .addCase(createOrganisation.fulfilled, (state, action) => {
        state.organisations.unshift(action.payload.organisation);
        state.pagination.total += 1;
      })
      .addCase(createOrganisation.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // update
    builder
      .addCase(updateOrganisation.fulfilled, (state, action) => {
        const idx = state.organisations.findIndex(o => o._id === action.payload._id);
        if (idx !== -1) state.organisations[idx] = action.payload;
        if (state.currentOrganisation?._id === action.payload._id) {
          state.currentOrganisation = action.payload;
        }
      })
      .addCase(updateOrganisation.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // changePlan / addAdmin / removeAdmin — update current + list
    const updateCurrentAndList = (state: OrganisationState, org: Organisation) => {
      const idx = state.organisations.findIndex(o => o._id === org._id);
      if (idx !== -1) state.organisations[idx] = org;
      if (state.currentOrganisation?._id === org._id) state.currentOrganisation = org;
    };

    builder
      .addCase(changeOrgPlan.fulfilled, (state, action) => updateCurrentAndList(state, action.payload))
      .addCase(changeOrgPlan.rejected, (state, action) => { state.error = action.payload as string; });

    builder
      .addCase(addOrgAdmin.fulfilled, (state, action) => updateCurrentAndList(state, action.payload))
      .addCase(addOrgAdmin.rejected, (state, action) => { state.error = action.payload as string; });

    builder
      .addCase(createAndAddOrgAdmin.fulfilled, (state, action) => updateCurrentAndList(state, action.payload))
      .addCase(createAndAddOrgAdmin.rejected, (state, action) => { state.error = action.payload as string; });

    builder
      .addCase(removeOrgAdmin.fulfilled, (state, action) => updateCurrentAndList(state, action.payload))
      .addCase(removeOrgAdmin.rejected, (state, action) => { state.error = action.payload as string; });

    // updateSettings — only patches settings on currentOrganisation
    builder
      .addCase(updateOrgSettings.fulfilled, (state, action) => {
        if (state.currentOrganisation) {
          state.currentOrganisation.settings = action.payload;
        }
      })
      .addCase(updateOrgSettings.rejected, (state, action) => { state.error = action.payload as string; });

    // domain add/remove — patch domains on currentOrganisation
    builder
      .addCase(addOrgDomain.fulfilled, (state, action) => {
        if (state.currentOrganisation) state.currentOrganisation.domains = action.payload.domains;
      })
      .addCase(addOrgDomain.rejected, (state, action) => { state.error = action.payload as string; });

    builder
      .addCase(removeOrgDomain.fulfilled, (state, action) => {
        if (state.currentOrganisation) state.currentOrganisation.domains = action.payload.domains;
      })
      .addCase(removeOrgDomain.rejected, (state, action) => { state.error = action.payload as string; });

    // suspend / reinstate
    builder
      .addCase(suspendOrganisation.fulfilled, (state, action) => {
        const org = state.organisations.find(o => o._id === action.payload);
        if (org) { org.isActive = false; org.billingStatus = 'suspended'; }
        if (state.currentOrganisation?._id === action.payload) {
          state.currentOrganisation.isActive = false;
          state.currentOrganisation.billingStatus = 'suspended';
        }
      })
      .addCase(suspendOrganisation.rejected, (state, action) => { state.error = action.payload as string; });

    builder
      .addCase(reinstateOrganisation.fulfilled, (state, action) => {
        const org = state.organisations.find(o => o._id === action.payload);
        if (org) { org.isActive = true; org.billingStatus = 'active'; }
        if (state.currentOrganisation?._id === action.payload) {
          state.currentOrganisation.isActive = true;
          state.currentOrganisation.billingStatus = 'active';
        }
      })
      .addCase(reinstateOrganisation.rejected, (state, action) => { state.error = action.payload as string; });
  },
});

export const { setFilters, clearFilters, clearError, clearCurrentOrganisation } = organisationSlice.actions;
export default organisationSlice.reducer;
